/**
 * Motion design tokens — single source for durations, easings, and class helpers.
 * Tailwind theme + CSS custom properties mirror these values.
 */

export const MOTION_DURATIONS = {
  fast: 150,
  standard: 250,
  enter: 300,
} as const;

/** Interactive transitions — soft deceleration, no snap at the start */
export const MOTION_EASE = 'cubic-bezier(0.33, 1, 0.68, 1)';

/** Page / overlay enter — gentle ease-out */
export const MOTION_EASE_ENTER = 'cubic-bezier(0.22, 1, 0.36, 1)';

/** Attention wiggles — symmetric in-out so oscillations ease naturally */
export const MOTION_EASE_IN_OUT = 'cubic-bezier(0.45, 0, 0.55, 1)';

export const MOTION_STORAGE_KEY = 'freelancecrm-motion-preference';

/** Tailwind duration utility class names */
export const motionDuration = {
  fast: 'duration-fast',
  standard: 'duration-standard',
  enter: 'duration-enter',
} as const;

/** Tailwind easing utility */
export const motionEase = 'ease-motion';
export const motionEaseEnter = 'ease-motion-enter';

/** Subtle hover lift (translate-y + shadow) */
export const motionHoverLift =
  'transition-[transform,box-shadow] duration-fast ease-motion hover:-translate-y-0.5 hover:shadow-md motion-safe:hover:-translate-y-0.5';

/** Animated focus ring. Yellow inner ring plus navy outer ring. */
export const motionFocusRing =
  'transition-[box-shadow] duration-fast ease-motion focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]';

/** Same indicator, drawn inside the control so overflow does not clip it. */
export const motionFocusRingInset =
  'transition-[box-shadow] duration-fast ease-motion focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring-inset)]';

/** Page/content entry */
export const motionPageEnter = 'animate-page-enter motion-safe:animate-page-enter';

/** Skeleton → content crossfade */
export const motionCrossfade = 'transition-opacity duration-standard ease-motion';

export type MotionPreference = 'system' | 'full' | 'reduced';

export function resolveReducedMotion(
  preference: MotionPreference,
  osPrefersReduced: boolean,
): boolean {
  if (preference === 'reduced') return true;
  if (preference === 'full') return false;
  return osPrefersReduced;
}

export function readOsReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Stable key for nested layouts — avoid re-entering when switching settings tabs. */
export function getPageTransitionKey(pathname: string): string {
  if (pathname.startsWith('/settings')) return '/settings';
  return pathname;
}
